import { EventBus } from "../../contexts";
import { logger, LOGS } from "../../utils";
import { encodeBase64 } from "../../utils/base64/base64";
import { ApiManager } from "../api";
import { LicenseState } from "../license/LicenseState";
import { ScoredItem, UserContext } from "./types/context";

export class UserContextLoader {
    private eventBus: EventBus;

    private apiManager: ApiManager;

    private licenseState: LicenseState;

    private currentContext?: UserContext;
    private fetchingUserContext: Promise<UserContext> | null = null;
    private contextHash: string = "";
    private contextRefreshTimer?: ReturnType<typeof setInterval>;
    private contextRefreshDate: string = ""; // YYYY-MM-DD de la dernière date connue

    constructor(
        eventBus: EventBus,
        apiManager: ApiManager,
        licenseState: LicenseState,
    ) {
        this.eventBus = eventBus;
        this.apiManager = apiManager;
        this.licenseState = licenseState;
    }

    public _cleanup(): void {
        this._stopRefreshTimer();
        this.currentContext = undefined;
        this.fetchingUserContext = null;
        this.contextHash = "";
        this.contextRefreshDate = "";
    }

    /**
     * Récupère le contexte utilisateur depuis le cache ou l'API backend.
     * Coalescence des appels concurrents : un seul fetch à la fois.
     * Le backend ne régénère le contexte qu'une fois par jour calendaire (isObsolete).
     *
     * @returns Le UserContext (personnes, titres, teams)
     */
    public async refreshUserContext(): Promise<UserContext> {
        logger.info(LOGS.UserManager, "refreshUserContext()");

        // Retourne le cache si disponible
        if (this.currentContext) {
            logger.info(
                LOGS.UserManager,
                "   => Return user context stored locally",
            );
            return this.currentContext;
        }

        // Pas de licence → contexte vide sans appel API
        if (!this.licenseState.hasValidLicense()) {
            logger.info(
                LOGS.UserManager,
                "   => No valid license — returning empty context",
            );
            const emptyContext: UserContext = {
                personnes: [],
                titres: [],
                teams: [],
            };
            this.currentContext = emptyContext;
            this._setContextHash(emptyContext);
            this.contextRefreshDate = new Date().toISOString().split("T")[0];
            this.eventBus.emit("startup-user-context-updated", emptyContext);
            return emptyContext;
        }

        // Si une requête est déjà en cours, attendre son résultat
        if (this.fetchingUserContext) {
            logger.info(
                LOGS.UserManager,
                "   => Waiting for ongoing context fetch...",
            );
            return this.fetchingUserContext;
        }

        logger.info(LOGS.UserManager, "   => Fetching new context from API");
        this.fetchingUserContext = this._fetchUserContext();

        try {
            const context = await this.fetchingUserContext;
            return context;
        } catch (error) {
            logger.error(
                LOGS.UserManager,
                `   => Error fetching user context: ${error}`,
            );
            throw new Error("Error fetching user context: " + error);
        } finally {
            this.fetchingUserContext = null;
        }
    }

    /**
     * Retourne le hash du contexte utilisateur courant (32 caractères).
     */
    public getUserContextHash(): string {
        if (this.contextHash === "") {
            logger.warn(
                LOGS.UserManager,
                "getUserContextHash - No context hash found yet",
            );
        }
        return this.contextHash;
    }

    /**
     * Vérifie si le contexte utilisateur a changé par rapport au hash précédent.
     */
    public hasContextChanged(previousHash: string): boolean {
        return previousHash !== this.getUserContextHash();
    }

    /**
     * Effectue l'appel API pour récupérer le contexte utilisateur.
     */
    private async _fetchUserContext(): Promise<UserContext> {
        logger.info(LOGS.UserManager, "_fetchUserContext()");

        let userContext: UserContext | null = null;

        try {
            const startTime = performance.now();
            userContext = await this.apiManager.getUserContext();
            logger.info(
                LOGS.Perfs,
                `    !!!  ApiManager getUserContext       : ${(performance.now() - startTime).toFixed(2)}ms`,
            );
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            logger.error(
                LOGS.UserManager,
                `Error in getUserContext : ${errorMessage}`,
            );

            throw error; // Propage l'erreur au lieu de return null
        }

        if (userContext) {
            userContext = this._normalizeUserContext(userContext);
            this.currentContext = userContext;
            this._setContextHash(userContext);
            this.contextRefreshDate = new Date().toISOString().split("T")[0];

            logger.info(LOGS.UserManager, "User context loaded", userContext);
            this.eventBus.emit("startup-user-context-updated", userContext);
        } else {
            logger.error(LOGS.UserManager, "   Received null context from API");
            throw new Error(
                "_fetchUserContext - Received null context from API",
            );
        }

        return userContext;
    }

    /**
     * Normalise un UserContext reçu de l'API.
     *
     * Le backend renvoie parfois un `ScoredItem` dont le champ `items` est
     * `undefined`/`null` (ou un bucket entier qui n'est pas un tableau), ce qui
     * viole le contrat de type `ScoredItem.items: string[]` et faisait planter les
     * consommateurs (ex. `CustomViewManager.generateViewFilter` → `items.forEach`).
     *
     * On corrige chaque valeur fautive en `[]` et on logue un `logger.warn` détaillé
     * (valeur reçue + valeur corrigée + emplacement) pour pouvoir remonter à la
     * source côté backend.
     *
     * @param context - Le contexte brut renvoyé par l'API
     * @returns Un contexte garanti conforme au contrat `UserContext`
     */
    private _normalizeUserContext(context: UserContext): UserContext {
        const normalizeBucket = (
            bucket: ScoredItem[] | undefined,
            bucketName: string,
        ): ScoredItem[] => {
            if (!Array.isArray(bucket)) {
                if (bucket !== undefined) {
                    logger.warn(
                        LOGS.UserManager,
                        `_normalizeUserContext - bucket '${bucketName}' invalide (reçu: ${JSON.stringify(bucket)}) → corrigé en []`,
                    );
                }
                return [];
            }

            return bucket.map((item, index) => {
                if (!Array.isArray(item?.items)) {
                    logger.warn(
                        LOGS.UserManager,
                        `_normalizeUserContext - '${bucketName}[${index}].items' invalide (reçu: ${JSON.stringify(item?.items)}, score: ${item?.score}) → corrigé en []`,
                    );
                    return { score: item?.score ?? 0, items: [] };
                }
                return item;
            });
        };

        return {
            personnes: normalizeBucket(context.personnes, "personnes"),
            titres: normalizeBucket(context.titres, "titres"),
            teams: normalizeBucket(context.teams, "teams"),
        };
    }

    /**
     * Calcule et stocke le hash du contexte utilisateur (base64, 32 chars).
     */
    private _setContextHash(context: UserContext): void {
        this.contextHash = context
            ? encodeBase64(JSON.stringify(context)).slice(0, 32)
            : "";
    }

    /**
     * Démarre un timer périodique (toutes les heures) qui vérifie si la date
     * calendaire a changé. Si oui, invalide le cache et rafraîchit le contexte.
     * Le backend ne régénère que si isObsolete() → pas de surcoût si même jour.
     */
    public startRefreshTimer(): void {
        this._stopRefreshTimer();

        const ONE_HOUR_MS = 60 * 60 * 1000;
        this.contextRefreshDate = new Date().toISOString().split("T")[0];

        this.contextRefreshTimer = setInterval(() => {
            const today = new Date().toISOString().split("T")[0];

            if (today !== this.contextRefreshDate) {
                logger.info(
                    LOGS.UserManager,
                    `Context refresh timer: date changed (${this.contextRefreshDate} → ${today}), refreshing user context`,
                );
                this.currentContext = undefined; // Invalide le cache pour forcer un nouveau fetch
                this.refreshUserContext().catch((error) => {
                    logger.error(
                        LOGS.UserManager,
                        `Context refresh timer: failed to refresh context: ${error}`,
                    );
                });
            }
        }, ONE_HOUR_MS);
    }

    /**
     * Arrête le timer de refresh du contexte.
     */
    private _stopRefreshTimer(): void {
        if (this.contextRefreshTimer) {
            clearInterval(this.contextRefreshTimer);
            this.contextRefreshTimer = undefined;
        }
    }
}
